export class ApiError extends Error {
    status;
    code;
    details;
    constructor(status, message, code, details) {
        super(message);
        this.status = status;
        this.code = code;
        this.details = details;
    }
}
export function createApiClient(opts) {
    const fetchFn = opts.fetchImpl ?? fetch;
    async function request(method, path, body, query) {
        const token = await opts.getAuthToken();
        // baseUrl may be origin-relative (e.g. '/api' behind the Vite proxy or the
        // Firebase Hosting rewrite); new URL() rejects relative URLs unless given a
        // base, so resolve against the page origin when one exists (browsers).
        // Non-browser callers (RN, Node) have no location and must pass an absolute
        // baseUrl — and an absolute baseUrl makes the base argument a no-op.
        const pageHref = globalThis.location?.href;
        const url = new URL(joinUrl(opts.baseUrl, path), pageHref);
        if (query) {
            for (const [k, v] of Object.entries(query)) {
                if (v !== undefined)
                    url.searchParams.set(k, String(v));
            }
        }
        const headers = {
            Accept: 'application/json',
        };
        if (token)
            headers['Authorization'] = `Bearer ${token}`;
        if (body !== undefined)
            headers['Content-Type'] = 'application/json';
        const res = await fetchFn(url.toString(), {
            method,
            headers,
            body: body === undefined ? undefined : JSON.stringify(body),
        });
        const text = await res.text();
        const json = text ? safeParse(text) : null;
        if (!res.ok) {
            const message = (json && typeof json === 'object' && 'message' in json && typeof json.message === 'string'
                ? json.message
                : null) ?? `Request failed: ${res.status}`;
            const code = json && typeof json === 'object' && 'code' in json && typeof json.code === 'string'
                ? json.code
                : undefined;
            throw new ApiError(res.status, message, code, json);
        }
        return json;
    }
    return {
        completeSignup(body) {
            return request('POST', '/auth/complete-signup', body);
        },
        me() {
            return request('GET', '/me');
        },
        createItem(body) {
            return request('POST', '/items', body);
        },
        rescanItem(itemId) {
            return request('POST', `/items/${itemId}/scan`);
        },
        finalizeRecipient(itemId, body) {
            return request('POST', `/items/${itemId}/finalize-recipient`, body);
        },
        expressInterest(itemId, body) {
            return request('POST', `/items/${itemId}/interests`, body);
        },
        withdrawInterest(itemId, charityId) {
            return request('DELETE', `/items/${itemId}/interests/${charityId}`);
        },
        updateWishlist(charityId, body) {
            return request('PUT', `/charities/${charityId}/wishlist`, body);
        },
        approveCharity(charityId, body) {
            return request('POST', `/admin/charities/${charityId}/approve`, body);
        },
        rejectCharity(charityId, body) {
            return request('POST', `/admin/charities/${charityId}/reject`, body);
        },
        pendingCharities() {
            return request('GET', '/admin/charities/pending');
        },
        adminUsers() {
            return request('GET', '/admin/users');
        },
        adminSetUserPassword(uid, body) {
            return request('POST', `/admin/users/${uid}/password`, body);
        },
        adminSetUsername(body) {
            return request('POST', '/admin/me/username', body);
        },
        siteDraft() {
            return request('GET', '/admin/site/draft');
        },
        saveSiteDraft(content) {
            return request('PUT', '/admin/site/draft', content);
        },
        discardSiteDraft() {
            return request('DELETE', '/admin/site/draft');
        },
        publishSite() {
            return request('POST', '/admin/site/publish');
        },
        siteVersions() {
            return request('GET', '/admin/site/versions');
        },
        restoreSiteVersion(versionId) {
            return request('POST', `/admin/site/versions/${versionId}/restore`);
        },
        publicMap() {
            return request('GET', '/public/map');
        },
        geocode(q) {
            return request('GET', '/geocode', undefined, { q });
        },
    };
}
function joinUrl(base, path) {
    const b = base.endsWith('/') ? base.slice(0, -1) : base;
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${b}${p}`;
}
function safeParse(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=client.js.map